import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Event } from '../../types/event.types';
import { useWouterRouter } from '../../hooks/useWouterNavigation';
import { useNavigationStore } from '../../stores/navigationStore';
import './card.css';
import './RecurringEventCard.css';
import { useSmartSeries } from '../../hooks/useSmartSeries';
import { groupDaysBySchedule } from '../../utils/seriesPatternUtils';
import { formatEventDateTime, formatEventTime } from '../../utils/timezoneUtils';
import {
  Tag,
  Icon,
  Button,
  FavoriteButton,
  ShareButton,
  DirectionsButton,
  AddToCalendarButton,
  NotificationBubble,
  LogoSVG,
  GlassPanel
} from '../../pages/DesignSystem';
import { SwipeableCard } from '../../pages/DesignSystem/patterns/Modal/BaseModal';
import { getTagFallbackGradient, sortTags } from '../../pages/DesignSystem/types/tags.types';
import { useDistance } from '../../hooks/useDistance';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useVisibilityTracker } from '../../hooks/useVisibilityTracker';
import { useFavorites, useHiddenEvents } from '../../stores/appStore';
import { SecurityUtils } from '../../utils/security';
import { optimizeCloudinaryUrl } from '../../services/cloudinary.service';

interface RecurringEventCardProps {
  mainEvent: Event;
  seriesEvents: Event[];
  userLocation?: { latitude: number; longitude: number };
  isMapView?: boolean;
  onDismiss?: (eventId: string) => void;
  onFavorite?: (eventId: string) => void;
  onClick?: (event: Event, seriesEvents: Event[]) => void;
  disableSwipe?: boolean;
  eventId: string;
  isFirstCard?: boolean; // Pour l'animation wiggle d'onboarding
  seriesPattern?: string | null; // Pattern pré-chargé en batch (évite useSmartSeries individuel)
  seriesDaySchedules?: Record<string, { start: string; end: string }> | null; // Per-day times from batch
}

// ✅ PERF FIX: Comparateur custom pour React.memo
const areRecurringCardPropsEqual = (prevProps: RecurringEventCardProps, nextProps: RecurringEventCardProps): boolean => {
  // Comparer mainEvent par ID + updatedAt
  const prevUpdatedAt = prevProps.mainEvent.updatedAt instanceof Date
    ? prevProps.mainEvent.updatedAt.getTime()
    : (prevProps.mainEvent.updatedAt || '');
  const nextUpdatedAt = nextProps.mainEvent.updatedAt instanceof Date
    ? nextProps.mainEvent.updatedAt.getTime()
    : (nextProps.mainEvent.updatedAt || '');

  if (prevProps.mainEvent.id !== nextProps.mainEvent.id || prevUpdatedAt !== nextUpdatedAt) {
    return false;
  }

  // Comparer seriesEvents par longueur
  if (prevProps.seriesEvents?.length !== nextProps.seriesEvents?.length) {
    return false;
  }

  // Comparer les autres props
  if (
    prevProps.userLocation?.latitude !== nextProps.userLocation?.latitude ||
    prevProps.userLocation?.longitude !== nextProps.userLocation?.longitude ||
    prevProps.isMapView !== nextProps.isMapView ||
    prevProps.disableSwipe !== nextProps.disableSwipe ||
    prevProps.isFirstCard !== nextProps.isFirstCard ||
    prevProps.eventId !== nextProps.eventId ||
    prevProps.seriesPattern !== nextProps.seriesPattern ||
    prevProps.seriesDaySchedules !== nextProps.seriesDaySchedules
  ) {
    return false;
  }

  return true;
};

const RecurringEventCard: React.FC<RecurringEventCardProps> = React.memo(({
  mainEvent,
  seriesEvents,
  userLocation,
  isMapView = false,
  onDismiss,
  onFavorite,
  onClick,
  disableSwipe = false,
  isFirstCard = false,
  seriesPattern,
  seriesDaySchedules,
}) => {
  const { navigate, location } = useWouterRouter();
  const { setEventModalState } = useNavigationStore();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  // Si seriesPattern est fourni (batch), on skip useSmartSeries (pas de Realtime inutile)
  const hasBatchPattern = seriesPattern !== undefined;
  const smartSeries = useSmartSeries(
    hasBatchPattern ? undefined : mainEvent.seriesId,
    undefined,
    hasBatchPattern ? undefined : mainEvent.organizerId
  );
  const pattern = hasBatchPattern ? seriesPattern : smartSeries.pattern;
  const seriesLoading = hasBatchPattern ? false : smartSeries.loading;
  const daySchedules = hasBatchPattern ? seriesDaySchedules : smartSeries.series?.daySchedules;
  const hasDaySchedules = daySchedules && Object.keys(daySchedules).length > 0;
  const [showNotif, setShowNotif] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;
  const analytics = useAnalytics();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { addToHiddenEvents } = useHiddenEvents();

  const imageRef = useRef<HTMLImageElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hasImage = Boolean(mainEvent.image);
  const isCancelled = mainEvent.status === 'cancelled';

  // Fallback: reveal card after timeout if image hasn't loaded yet
  useEffect(() => {
    if (!hasImage || imageLoaded) return;
    const timer = setTimeout(() => setImageLoaded(true), 1500);
    return () => clearTimeout(timer);
  }, [hasImage, imageLoaded]);

  // Charger TOUTES les occurrences de la série depuis favorite_events
  const [fullSeriesEvents, setFullSeriesEvents] = useState<Event[]>(seriesEvents);

  useEffect(() => {
    if (!mainEvent.seriesId) {
      setFullSeriesEvents(seriesEvents);
      return;
    }

    const loadFullSeries = async () => {
      const { locationCache } = await import('../../services/LocationCacheService');
      const allFavorites = await locationCache.getFavoriteEventsFromDB();
      const seriesOccurrences = allFavorites.filter(event => event.seriesId === mainEvent.seriesId);

      if (seriesOccurrences && seriesOccurrences.length > 0) {
        setFullSeriesEvents(seriesOccurrences);
      } else {
        setFullSeriesEvents(seriesEvents);
      }
    };

    loadFullSeries();
  }, [mainEvent.seriesId, seriesEvents]);

  const sortedEvents = useMemo(() => {
    const now = new Date();
    return fullSeriesEvents
      .filter(event => {
        if (!event.datetime) return false;
        const eventDate = new Date(event.datetime);
        return eventDate > now;
      })
      .sort((a, b) => {
        if (!a.datetime || !b.datetime) return 0;
        const dateA = new Date(a.datetime);
        const dateB = new Date(b.datetime);
        return dateA.getTime() - dateB.getTime();
      });
  }, [fullSeriesEvents]);

  // Optimized image URL for card display (800px max width)
  const optimizedImageUrl = useMemo(
    () => optimizeCloudinaryUrl(mainEvent.image, { width: 800 }),
    [mainEvent.image]
  );

  // Lazy loading de l'image
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && imageRef.current) {
            imageRef.current.src = optimizedImageUrl;
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '300px' }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => observer.disconnect();
  }, [optimizedImageUrl]);

  // Visibility tracking: 80% threshold, accumulated duration, sent on unmount
  useVisibilityTracker(cardRef, mainEvent, 'card');

  // ✅ PERF FIX: Extraire les valeurs stables de mainEvent
  const mainEventId = mainEvent.id;
  const mainEventSeriesId = mainEvent.seriesId;

  const distance = useDistance(userLocation, {
    latitude: mainEvent.latitude,
    longitude: mainEvent.longitude
  });

  const handleClick = useCallback(() => {
    analytics.trackEventClick(mainEvent, 'card');

    if (onClick) {
      onClick(mainEvent, sortedEvents);
    } else {
      const currentPath = location.pathname;
      const basePath = currentPath.startsWith('/favorites') ? '/favorites' : '';

      // ✅ Navigation intelligente : replace si on change d'event, push si nouvelle ouverture
      const isChangingEvent = currentPath.startsWith('/event/') || currentPath.startsWith('/series/');

      setEventModalState({
        isMapView,
        userLocation: userLocation ? { lat: userLocation.latitude, lng: userLocation.longitude } : null,
        multiEvents: [mainEvent],
        enrichedEvents: [{ main: mainEvent, series: sortedEvents }]
      });

      navigate(`${basePath}/event/${mainEventId}`, {
        replace: isChangingEvent  // Replace si on change d'event, push si nouvelle ouverture
      });
    }
  }, [mainEvent, sortedEvents, onClick, navigate, isMapView, userLocation, location.pathname, analytics, setEventModalState, mainEventId]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleExpandImage = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMobile) {
      setShowLightbox(true);
    } else {
      window.open(mainEvent.image || '', '_blank');
    }
  }, [isMobile, mainEvent.image]);

  const handleFavorite = useCallback(async () => {
    if (onFavorite) {
      onFavorite(mainEventSeriesId || mainEventId);
    }
  }, [onFavorite, mainEventSeriesId, mainEventId]);

  // Simplifier le pattern de récurrence (retirer "Every" et les heures, garder "until")
  const simplifiedPattern = useMemo(() => {
    if (!pattern) return 'Recurring';

    // Extraire la partie "until..." si elle existe
    const untilMatch = pattern.match(/\s*(until\s+.+)$/i);
    const untilPart = untilMatch ? ` ${untilMatch[1]}` : '';

    // Gérer les patterns "Every day" déjà formatés par useSmartSeries
    // (7 jours = "Every day", 5-6 jours = "Every day <strong>except</strong> ...")
    if (/^Every\s+day\b/i.test(pattern)) {
      // Extraire la partie descriptive (avant les heures)
      let dayPart = pattern.replace(/\s+at\s+\d{1,2}:\d{2}.*/i, '');
      dayPart = dayPart.replace(/\s+until\s+.+$/i, '').trim();
      return `${dayPart}${untilPart}`;
    }

    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    // Trouver les jours mentionnés dans le pattern
    const mentionedDays = allDays.filter(day =>
      new RegExp(`\\b${day}\\b`, 'i').test(pattern)
    );

    // Si 5 jours ou plus, afficher "Every day except..."
    if (mentionedDays.length >= 5) {
      const missingDays = allDays.filter(day => !mentionedDays.includes(day));
      if (missingDays.length === 0) {
        return `Every day${untilPart}`;
      } else if (missingDays.length === 1) {
        return `Every day except ${missingDays[0]}${untilPart}`;
      } else {
        const lastDay = missingDays.pop();
        return `Every day except ${missingDays.join(', ')} and ${lastDay}${untilPart}`;
      }
    }

    // Sinon, simplifier normalement (garder "Every" devant les jours)
    let simplified = pattern;
    // Strip daySchedules parenthetical (e.g., "(Tue, Thu 09:00 - 10:00 · Sat 04:00)")
    simplified = simplified.replace(/\s*\([^)]*\d{1,2}:\d{2}[^)]*\)/g, '');
    // Retirer toutes les heures sous différents formats:
    // "at HH:MM", "- HH:MM", "HH:MM" seul avant "until"
    simplified = simplified.replace(/\s*[-–]\s*\d{1,2}:\d{2}/gi, '');
    simplified = simplified.replace(/\s+at\s+\d{1,2}:\d{2}(\s*(AM|PM))?/gi, '');
    // Nettoyer les espaces doubles
    simplified = simplified.replace(/\s+/g, ' ').trim();
    return simplified || 'Recurring';
  }, [pattern]);

  // Formater le pattern avec les jours en gras
  const formattedPattern = useMemo(() => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    // Nettoyer les <strong> existants (de useSmartSeries) pour re-formater uniformément
    let result = simplifiedPattern.replace(/<\/?strong>/g, '');

    // Mettre "Every day" en gras
    result = result.replace(/\bEvery day\b/g, '<strong>Every day</strong>');

    // Mettre "except" en gras
    result = result.replace(/\bexcept\b/gi, '<strong>except</strong>');

    // Remplacer chaque jour par une version avec balise
    days.forEach(day => {
      const regex = new RegExp(`\\b${day}\\b`, 'gi');
      result = result.replace(regex, `<strong>${day}</strong>`);
    });

    return result;
  }, [simplifiedPattern]);

  // Obtenir les heures de l'événement — approche adaptative selon la complexité des horaires
  // 1. Si daySchedules existe (nouvelle feature) → utiliser directement
  // 2. Sinon, déduire depuis les occurrences (legacy events)
  // Résultat: 1 groupe → "07:30 - 09:00", 2-3 → "Mon, Fri 07:30 · Wed 08:00", 4+ → "Various times"
  const eventTime = useMemo(() => {
    // Helper: format adaptive groups display
    const formatGroups = (groups: Array<{ days: string[]; start: string; end: string }>) => {
      if (groups.length === 1) {
        return `${groups[0].start}${groups[0].end ? ` - ${groups[0].end}` : ''}`;
      } else if (groups.length <= 3) {
        return groups.map(g => {
          const days = g.days.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
          return `${days} ${g.start}${g.end ? ` - ${g.end}` : ''}`;
        }).join(' · ');
      } else {
        return 'Various times';
      }
    };

    // Path 1: daySchedules from series (new events)
    if (hasDaySchedules && daySchedules) {
      return formatGroups(groupDaysBySchedule(daySchedules));
    }

    // Path 2: Deduce from occurrences (legacy events without daySchedules)
    if (sortedEvents.length >= 2) {
      const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const timesByDay = new Map<string, { start: string; end: string }>();

      for (const evt of sortedEvents) {
        if (!evt.datetime) continue;
        const d = new Date(evt.datetime);
        const dayName = DAY_NAMES[d.getDay()];
        if (timesByDay.has(dayName)) continue; // first occurrence per day wins
        const start = formatEventTime(evt.datetime, evt.timezone);
        const end = evt.endTime ? formatEventTime(evt.endTime, evt.timezone) : '';
        timesByDay.set(dayName, { start, end });
      }

      // Check if all days share the same time
      const uniqueTimes = new Set(Array.from(timesByDay.values()).map(v => `${v.start}|${v.end}`));
      if (uniqueTimes.size > 1) {
        // Variable times detected — build groups from occurrences
        const scheduleRecord: Record<string, { start: string; end: string }> = {};
        timesByDay.forEach((v, k) => { scheduleRecord[k] = v; });
        return formatGroups(groupDaysBySchedule(scheduleRecord));
      }
    }

    // Default: single time from mainEvent
    return mainEvent.datetime
      ? `${formatEventTime(mainEvent.datetime, mainEvent.timezone)}${mainEvent.endTime ? ` - ${formatEventTime(mainEvent.endTime, mainEvent.timezone)}` : ''}`
      : '';
  }, [hasDaySchedules, daySchedules, sortedEvents, mainEvent.datetime, mainEvent.endTime, mainEvent.timezone]);

  // ✅ PERF FIX: Mémoiser les éléments de rendu
  const tagsElement = useMemo(() => (
    <div className="event-card__tags">
      {sortTags(mainEvent.tags).map(tag => (
        <Tag
          key={tag}
          label={tag}
          type="activity"
          size="md"
        />
      ))}
    </div>
  ), [mainEvent.tags]);

  const headerElement = useMemo(() => (
    <div className="event-card__header">
      {tagsElement}
      <div className="event-card__actions" onClick={e => e.stopPropagation()}>
        <ShareButton
          shareData={{
            title: mainEvent.title,
            text: `what2do.today\nCome discover ${mainEvent.title}`,
            url: window.location.href
          }}
          event={mainEvent}
          analyticsContext="card"
          variant="icon"
          size="lg"
          shouldTriggerSuccess={false}
          onShareSuccess={() => setShowNotif(true)}
        />
        <FavoriteButton
          id={mainEvent.id}
          seriesId={mainEvent.seriesId}
          organizerId={mainEvent.organizerId}
          expiresAt={mainEvent.datetime}
          seriesEvents={sortedEvents}
          fullEvent={mainEvent}
          onFavoriteChange={handleFavorite}
          size="lg"
          analyticsContext="card"
        />
      </div>
    </div>
  ), [tagsElement, mainEvent.title, mainEvent.id, mainEvent.seriesId, mainEvent.organizerId, mainEvent.datetime, sortedEvents, handleFavorite]);

  // ✅ PERF FIX: Mémoiser le prix
  const priceElement = useMemo(() => {
    // No prices at all → show nothing (scraped events with unknown price)
    if (!mainEvent.prices || mainEvent.prices.length === 0) {
      return null;
    }

    // Check if all prices have amount === 0 → Free
    const allFree = mainEvent.prices.every(price => price.amount === 0);
    if (allFree) {
      return <span className="event-card__glass-price event-card__glass-price--free">Free</span>;
    }

    // Sort prices: null amounts last, then by amount ascending
    const sortedPrices = [...mainEvent.prices].sort((a, b) => {
      if (a.amount === null && b.amount === null) return 0;
      if (a.amount === null) return 1;
      if (b.amount === null) return -1;
      return a.amount - b.amount;
    });

    return (
      <>
        {sortedPrices.map((price, index) => (
          <span key={index} className={`event-card__glass-price${price.amount === null ? ' event-card__glass-price--info' : ''}`}>
            {price.amount === null
              ? (price.description || 'Contact organizer')
              : price.amount === 0
                ? 'Free'
                : `${price.amount}${price.currency === 'EUR' ? '€' : price.currency}`}
            {price.amount !== null && price.description && ` - ${price.description}`}
          </span>
        ))}
      </>
    );
  }, [mainEvent.prices]);

  // Déterminer le statut d'une occurrence
  const getOccurrenceStatus = useCallback((event: Event) => {
    if (event.isException) return { label: 'Modified', className: 'modified' };
    return { label: 'Upcoming', className: 'upcoming' };
  }, []);

  // ✅ PERF FIX: Ne rendre la liste des dates QUE quand isExpanded (DOM vide quand fermé)
  const recurrencesListElement = useMemo(() => (
    <div
      className={`recurring-card__dates-list ${isExpanded ? 'recurring-card__dates-list--expanded' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {isExpanded && sortedEvents.map((event) => {
        const status = getOccurrenceStatus(event);
        return (
          <div
            key={event.id}
            className="recurring-card__date-item"
          >
            <div className="recurring-card__date-info">
              <span className="recurring-card__date">
                {formatEventDateTime(event.datetime, event.timezone).date}
              </span>
              <span className="recurring-card__date-time">
                {formatEventTime(event.datetime, event.timezone)}{event.endTime ? ` - ${formatEventTime(event.endTime, event.timezone)}` : ''}
              </span>
            </div>
            <div className="recurring-card__date-right">
              <span className={`recurring-card__status-text recurring-card__status-text--${status.className}`}>
                {status.label}
              </span>
              <AddToCalendarButton
                event={event}
                analyticsContext="card"
                iconSize={14}
              />
            </div>
          </div>
        );
      })}
    </div>
  ), [sortedEvents, isExpanded, getOccurrenceStatus]);

  // ========== RENDU AVEC IMAGE ==========
  const renderWithImage = () => (
    <div
      ref={cardRef}
      className={`event-card event-card--with-image event-card--recurring ${isCancelled ? 'event-card--cancelled' : ''} ${imageLoaded ? 'event-card--revealed' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${mainEvent.title}`}
    >
      {/* Image de fond */}
      <div className="event-card__background">
        <img
          ref={imageRef}
          alt={mainEvent.title}
          className={imageLoaded ? 'loaded' : ''}
          onLoad={handleImageLoad}
          loading="lazy"
        />
      </div>

      {/* Header avec tags et actions - ✅ PERF: utilise élément mémorisé */}
      {headerElement}

      {/* Spacer pour pousser le GlassPanel vers le bas + bouton expand */}
      <div className="event-card__spacer">
        <button
          className="event-card__expand-image"
          onClick={handleExpandImage}
          aria-label="Agrandir l'image"
        >
          <Icon name="Fullscreen" size={20} />
        </button>
      </div>

      {/* GlassPanel avec les infos */}
      <div className="event-card__glass-container">
        <GlassPanel variant="light" blur="md" radius="md">
          <div className="event-card__glass-content">
            {/* Badge Annulé si l'event est cancelled */}
            {isCancelled && (
              <span className="event-card__cancelled-badge">Cancelled</span>
            )}
            {/* Titre */}
            <h3 className={`event-card__glass-title ${isCancelled ? 'event-card__glass-title--cancelled' : ''}`}>{mainEvent.title}</h3>

            {/* Récurrence pattern + chevron pour ouvrir la liste */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="Repeat" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-info">
                  <span
                    className="event-card__glass-secondary"
                    dangerouslySetInnerHTML={{ __html: SecurityUtils.sanitizeHtml(seriesLoading ? 'Loading...' : formattedPattern) }}
                  />
                  <span className="event-card__glass-secondary">
                    {eventTime}
                  </span>
                </div>
                {sortedEvents.length > 0 && (
                  <Button
                    variant="glass"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                    aria-label={isExpanded ? "Hide dates" : "Show dates"}
                  >
                    <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
                  </Button>
                )}
              </div>
            </div>

            {/* Liste des récurrences (dropdown) - ✅ PERF: utilise élément mémorisé */}
            {recurrencesListElement}

            {/* Lieu + distance + navigation */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="MapPin" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-location-text">
                  <span className="event-card__glass-location-name">
                    {mainEvent.locationName || mainEvent.location}
                  </span>
                  {distance && (
                    <span className="event-card__glass-location-distance">
                      · {distance}
                    </span>
                  )}
                </div>
                {mainEvent.latitude && mainEvent.longitude && (
                  <DirectionsButton
                    latitude={mainEvent.latitude}
                    longitude={mainEvent.longitude}
                    locationName={mainEvent.locationName || mainEvent.location || 'Event'}
                    event={mainEvent}
                    analyticsContext="card"
                  />
                )}
              </div>
            </div>

            {/* Prix */}
            {priceElement && (
              <div className="event-card__glass-row">
                <div className="event-card__glass-icon">
                  <Icon name="Ticket" size={20} />
                </div>
                <div className="event-card__glass-info">
                  {priceElement}
                </div>
              </div>
            )}
          </div>
        </GlassPanel>
      </div>
    </div>
  );

  // Dégradé de fond basé sur les tags (cards sans image)
  const fallbackGradient = useMemo(() => getTagFallbackGradient(mainEvent.tags), [mainEvent.tags]);

  // ========== RENDU SANS IMAGE (fallback) ==========
  const renderWithoutImage = () => (
    <div
      ref={cardRef}
      className={`event-card event-card--with-image event-card--fallback event-card--recurring event-card--revealed ${isCancelled ? 'event-card--cancelled' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${mainEvent.title}`}
    >
      {/* Logo en fond - dégradé teinté selon les tags de l'event */}
      <div
        className="event-card__background event-card__background--fallback"
        style={fallbackGradient ? { background: fallbackGradient } : undefined}
      >
        <LogoSVG
          variant="vertTexte"
          size="auto"
          padding={0}
        />
      </div>

      {/* Header avec tags et actions - ✅ PERF: utilise élément mémorisé */}
      {headerElement}

      {/* Spacer pour pousser le GlassPanel vers le bas */}
      <div className="event-card__spacer" />

      {/* GlassPanel avec les infos */}
      <div className="event-card__glass-container">
        <GlassPanel variant="light" blur="md" radius="md">
          <div className="event-card__glass-content">
            {/* Badge Annulé si l'event est cancelled */}
            {isCancelled && (
              <span className="event-card__cancelled-badge">Cancelled</span>
            )}
            {/* Titre */}
            <h3 className={`event-card__glass-title ${isCancelled ? 'event-card__glass-title--cancelled' : ''}`}>{mainEvent.title}</h3>

            {/* Récurrence pattern + chevron pour ouvrir la liste */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="Repeat" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-info">
                  <span
                    className="event-card__glass-secondary"
                    dangerouslySetInnerHTML={{ __html: SecurityUtils.sanitizeHtml(seriesLoading ? 'Loading...' : formattedPattern) }}
                  />
                  <span className="event-card__glass-secondary">
                    {eventTime}
                  </span>
                </div>
                {sortedEvents.length > 0 && (
                  <Button
                    variant="glass"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                    aria-label={isExpanded ? "Hide dates" : "Show dates"}
                  >
                    <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
                  </Button>
                )}
              </div>
            </div>

            {/* Liste des récurrences (dropdown) - ✅ PERF: utilise élément mémorisé */}
            {recurrencesListElement}

            {/* Lieu + distance + navigation */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="MapPin" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-location-text">
                  <span className="event-card__glass-location-name">
                    {mainEvent.locationName || mainEvent.location}
                  </span>
                  {distance && (
                    <span className="event-card__glass-location-distance">
                      · {distance}
                    </span>
                  )}
                </div>
                {mainEvent.latitude && mainEvent.longitude && (
                  <DirectionsButton
                    latitude={mainEvent.latitude}
                    longitude={mainEvent.longitude}
                    locationName={mainEvent.locationName || mainEvent.location || 'Event'}
                    event={mainEvent}
                    analyticsContext="card"
                  />
                )}
              </div>
            </div>

            {/* Prix */}
            {priceElement && (
              <div className="event-card__glass-row">
                <div className="event-card__glass-icon">
                  <Icon name="Ticket" size={20} />
                </div>
                <div className="event-card__glass-info">
                  {priceElement}
                </div>
              </div>
            )}
          </div>
        </GlassPanel>
      </div>
    </div>
  );

  return (
    <SwipeableCard
      onLike={async () => {
        const storageId = mainEvent.seriesId || mainEvent.id;

        if (isFavorite(storageId)) {
          return;
        }

        await toggleFavorite(storageId, undefined, undefined, mainEvent, sortedEvents);
        handleFavorite();
      }}
      onDismiss={async () => {
        await addToHiddenEvents(mainEvent.seriesId || mainEvent.id, 'dismiss');
        if (onDismiss) {
          onDismiss(mainEvent.seriesId || mainEvent.id);
        }
      }}
      eventId={mainEvent.id}
      seriesId={mainEvent.seriesId}
      organizerId={mainEvent.organizerId}
      fullEvent={mainEvent}
      disableSwipe={disableSwipe}
      isFirstCard={isFirstCard}
      isVisible={!isMapView}
    >
      {hasImage ? renderWithImage() : renderWithoutImage()}

      <NotificationBubble
        show={showNotif}
        message="Link copied! ✨"
        type="success"
        duration={3000}
        onHide={() => setShowNotif(false)}
      />

      {/* Lightbox pour affichage plein écran sur mobile */}
      {showLightbox && mainEvent.image && (
        <div
          className="event-card__lightbox"
          onClick={() => setShowLightbox(false)}
        >
          <button
            className="event-card__lightbox-close"
            onClick={() => setShowLightbox(false)}
            aria-label="Fermer"
          >
            <Icon name="X" size={24} />
          </button>
          <img
            src={optimizeCloudinaryUrl(mainEvent.image, { width: 1200 })}
            alt={mainEvent.title}
            className="event-card__lightbox-image"
          />
        </div>
      )}
    </SwipeableCard>
  );
}, areRecurringCardPropsEqual);

export default RecurringEventCard;
